import { Suspense, lazy, useMemo, useState } from 'react';
import { FUND_IDS, type FundId } from '../core/types.ts';
import { BurnLog } from './components/BurnLog.tsx';
import { CombinedStats } from './components/CombinedStats.tsx';
import { EventFeed } from './components/EventFeed.tsx';
import { FlowExplainer } from './components/FlowExplainer.tsx';
import { FundCard } from './components/FundCard.tsx';
import { Header } from './components/Header.tsx';
import { PositionsTable } from './components/PositionsTable.tsx';
import { PriceStrip } from './components/PriceStrip.tsx';
import { StrategyTable } from './components/StrategyTable.tsx';
import { TradeHistory } from './components/TradeHistory.tsx';
import { Skeleton } from './components/ui.tsx';
import { useNow, useSnapshot } from './lib/useSnapshot.ts';

/**
 * recharts is most of the bundle, and this is a page people leave open. Loading
 * the chart separately lets the numbers paint without waiting on it.
 */
const EquityChart = lazy(() =>
  import('./components/EquityChart.tsx').then((m) => ({ default: m.EquityChart })),
);

type Filter = 'both' | FundId;

/** The engine ticks once a minute, so anything older than this is suspicious. */
const STALE_AFTER_MS = 150_000;

function FilterTabs({
  value,
  onChange,
  labels,
}: {
  value: Filter;
  onChange: (f: Filter) => void;
  labels: Record<FundId, { name: string; logo: string }>;
}) {
  const options: Array<{ id: Filter; label: string; logo?: string }> = [
    { id: 'both', label: 'Both books' },
    ...FUND_IDS.map((id) => ({ id: id as Filter, label: labels[id].name, logo: labels[id].logo })),
  ];

  return (
    <div className="flex gap-1 rounded-lg border border-edge bg-panel p-1">
      {options.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          aria-pressed={value === o.id}
          className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-medium transition ${
            value === o.id ? 'bg-panel-2 text-white shadow-sm' : 'text-dim hover:text-muted'
          }`}
        >
          {o.logo && <img src={o.logo} alt="" className="size-4 rounded-full" width={16} height={16} />}
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function App() {
  const { data: snap, error, loading, failures } = useSnapshot();
  const now = useNow();
  const [filter, setFilter] = useState<Filter>('both');

  const stale = Boolean(snap && snap.lastTickAt > 0 && now - snap.lastTickAt > STALE_AFTER_MS);

  const selected = useMemo(() => {
    if (!snap) return [];
    return filter === 'both' ? FUND_IDS.map((id) => snap.funds[id]) : [snap.funds[filter]];
  }, [snap, filter]);

  const labels = useMemo(() => {
    if (!snap) return { short: { name: 'Shortcoin', logo: '' }, long: { name: 'Longcoin', logo: '' } };
    return {
      short: { name: snap.funds.short.name, logo: snap.funds.short.logo },
      long: { name: snap.funds.long.name, logo: snap.funds.long.logo },
    };
  }, [snap]);

  return (
    <div className="min-h-screen pb-16">
      <Header snap={snap} now={now} stale={stale} />

      <main className="mx-auto flex max-w-[1500px] flex-col gap-4 px-4 pt-4 sm:px-6">
        {error && failures > 1 && (
          <div className="rounded-lg border border-down/40 bg-down/10 px-4 py-2.5 text-[12px] text-down">
            Lost contact with the engine ({error}). Retrying every few seconds — the numbers below are the last known
            state.
          </div>
        )}

        {snap?.mode === 'paper' && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/8 px-4 py-2.5 text-[12px] text-amber-200/90">
            <strong className="font-semibold">Paper mode.</strong> Prices, positions and P&L are real and live, but
            fills are simulated and no funds are at risk. Nothing here is financial advice.
          </div>
        )}

        <CombinedStats snap={snap} />
        <PriceStrip prices={snap?.prices ?? null} />

        {loading || !snap ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <Skeleton className="h-[290px]" />
            <Skeleton className="h-[290px]" />
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            <FundCard fund={snap.funds.short} />
            <FundCard fund={snap.funds.long} />
          </div>
        )}

        <Suspense fallback={<Skeleton className="h-[430px]" />}>
          <EquityChart snap={snap} />
        </Suspense>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm font-semibold tracking-wide text-muted uppercase">Book detail</h2>
          <FilterTabs value={filter} onChange={setFilter} labels={labels} />
        </div>

        <PositionsTable funds={selected} />

        {/* min-w-0 is load-bearing: grid and flex children default to
            min-width:auto, so without it these columns stretch to their widest
            table's min-width and push the whole page sideways on a phone
            instead of letting each table's own scroll wrapper handle it. */}
        <div className="grid items-start gap-4 xl:grid-cols-[1.35fr_1fr]">
          <div className="flex min-w-0 flex-col gap-4">
            <TradeHistory funds={selected} now={now} />
            <BurnLog funds={selected} now={now} />
            <StrategyTable snap={snap} />
          </div>
          <div className="flex min-w-0 flex-col gap-4">
            <EventFeed snap={snap} now={now} />
            <FlowExplainer snap={snap} />
          </div>
        </div>

        <footer className="mt-4 border-t border-edge pt-5 text-[11px] leading-relaxed text-dim">
          <p className="mb-2">
            Shortcoin and Longcoin are two pump.fun tokens whose creator fees fund opposing leveraged books. Twenty-five percent of every realized profit buys and burns that coin's own supply; the other
            seventy-five percent is redeployed. The engine reports every action it takes on this page.
          </p>
          <p>
            Leveraged trading can lose money, including all of it. These are memecoins, not investment products, and
            nothing on this page is financial advice. Strategy v{snap?.strategyVersion ?? '—'}
            {snap ? ` · ${snap.tickCount.toLocaleString()} ticks since launch` : ''}.
          </p>
        </footer>
      </main>
    </div>
  );
}
